import React, { useEffect, useRef, useState } from 'react'
import { connectToParent } from 'penpal'
import { RegistrationStatusWithDisabled } from './SipConn'
import { PauseService } from './Pause'
import { AsyncMethodReturns, FunctionPropertyNames } from 'penpal/lib/types'
import { ChildApi } from '~/embed'
import { Chat } from './chatConn/types'
import { useIntl } from 'react-intl'

export interface ParentApi {
  loaded: () => void
  close: () => void
  open: () => void
  goToClient: (id: number, externalId: string, isAuxClick: boolean) => void
  goToURL: (link: CustomLink, isAuxClick: boolean) => void
  createTicket: (chat: Chat) => void
  startedCall: () => void
  endedCall: () => void
  establishedAt: (date: Date) => void
  setSipRegistrationStatus: (status: RegistrationStatusWithDisabled) => void
  setChatLoading: React.Dispatch<React.SetStateAction<boolean>>
  setLocale: React.Dispatch<React.SetStateAction<string>>
  setPhoneEnabled: React.Dispatch<React.SetStateAction<boolean>>
  pauseEnabled: (service: PauseService) => void
  pauseDisabled: (service: PauseService) => void
  notificationsCheck: () => NotificationPermission
  notificationsAsk: () => Promise<NotificationPermission>
  notify: (body: string, title?: string) => void
  setUnreadCount: (val: number) => void
  setDragHandlerRect: (rect: DOMRect) => void
}

export type IframeContextState = {
  isLoaded: boolean // możliwe że jest niepotrzebny - sprawdić na SipConn
  makeCallRef: React.MutableRefObject<
    (number: string, payload?: object | undefined) => void
  >
  pauseEnableRef: React.MutableRefObject<(service: PauseService) => void>
  pauseDisableRef: React.MutableRefObject<(service: PauseService) => void>
  parentRef: React.MutableRefObject<
    AsyncMethodReturns<ParentApi, FunctionPropertyNames<ParentApi>> | undefined
  >
}

export const IframeContext = React.createContext<IframeContextState>({
  isLoaded: false,
  makeCallRef: { current: () => {} },
  pauseEnableRef: { current: () => {} },
  pauseDisableRef: { current: () => {} },
  parentRef: { current: undefined },
})

type Props = {
  children: React.ReactNode
}

const IframeConn: React.FC<Props> = ({ children }) => {
  if (!window.isIframe) {
    return <>{children}</>
  }

  const [isLoaded, setLoaded] = useState(false)

  const parentRef =
    useRef<AsyncMethodReturns<ParentApi, FunctionPropertyNames<ParentApi>>>()

  const makeCallRef = useRef<
    (number: string, payload?: object | undefined) => void
  >(() => {})
  const pauseEnableRef = useRef<(service: PauseService) => void>(() => {})
  const pauseDisableRef = useRef<(service: PauseService) => void>(() => {})

  useEffect(() => {
    const iframeConn = connectToParent<ParentApi>({
      methods: {
        makeCall(number: string, payload: object) {
          if (parentRef.current) {
            parentRef.current.open()
            makeCallRef.current(number, payload)
          }
        },

        pauseEnable(service: PauseService) {
          pauseEnableRef.current(service)
        },

        pauseDisable(service: PauseService) {
          pauseDisableRef.current(service)
        },
      } as ChildApi,
    })

    iframeConn.promise.then(parent => {
      parentRef.current = parent

      parent.loaded()

      setLoaded(true)

      parent.setPhoneEnabled(window.c.phoneEnabled)
      parent.setLocale(window.intl.locale)

      if (!window.c.phoneEnabled) {
        parent.setSipRegistrationStatus('disabled')
      }

      if (!window.c.chatEnabled) {
        parent.setChatLoading(false)
      }
    })
  }, [])

  const { locale } = useIntl()

  useEffect(() => {
    if (parentRef.current) {
      parentRef.current.setLocale(locale)
    }
  }, [locale])

  const state = {
    isLoaded,
    parentRef,
    makeCallRef,
    pauseEnableRef,
    pauseDisableRef,
  }

  return (
    <IframeContext.Provider value={state}>
      {isLoaded ? children : null}
    </IframeContext.Provider>
  )
}

export default IframeConn
